import { observer } from "mobx-react";
import React, { FC, ReactNode } from "react"
import Box from "react-raster";
import { Button, Icon, Paper, Text, Tooltip } from "src/components";
import { useTranslation } from "react-i18next";
import { Data, User } from "src/models";
import { IData } from "src/types";
import styled from "styled-components";
import { EmptyCard } from "./cards";
import { theme } from "src/config";

export interface ISectionProps {
  currentUser:    User,
  data:           Data<IData>[],
  dataAlt?:       Data<IData>[],
  addData?:       Data<IData>[],
  addDataAlt?:    Data<IData>[],
  title:          string,
  linkTitle?:     string,
  newToolTip?:    string,
  newToolTipAlt?: string,
  newIcon?:       ReactNode,
  newIconAlt?:    ReactNode,
  space_id?:      number,
  Card:           FC<{data: any, onClick?(): void, onUnlink?(): void, space_id?: number}>,
  CardAlt?:       FC<{data: any, onClick?(): void, onUnlink?(): void, space_id?: number}>,
  onNew?   (): void,
  onNewAlt?(): void,
  sort?   (lhs: Data<IData>, rhs: Data<IData>): number,
  sortAlt?(lhs: Data<IData>, rhs: Data<IData>): number,
  onAdd?      (data: Data<IData>): void,
  onAddAlt?   (data: Data<IData>): void,
  onRemove?   (data: IData):       void,
  onRemoveAlt?(data: Data<IData>): void,
}

const SectionPaper = styled(Paper)`
  position: relative;
  margin:   2rem 0rem;
`;

export const CardContainer = styled.div`
  display:        flex;
  flex-wrap:      wrap;
  flex-direction: row;
  column-gap:     1.5rem;
  row-gap:        1.5rem;
`;

const StyledText = styled(Text)`
  font-size:     ${theme.font.size.large};
  font-weight:   ${theme.font.weight.bold};
  margin-bottom: 2rem;
`

const AddButton = styled(Paper)`
  z-index:         1;
  border-radius:   50%;
  position:        absolute;
  padding:         0;
  align-items:     center;
  justify-content: center;
  display:         flex;
  bottom:          0.5rem;
  right:           0;
  box-shadow:      none;
  transform:       translate(-50%, 50%);
  background:      ${theme.color.buttons.primary};
`;

const ViewMoreButton = styled(Button)`
  border-radius: 50%;
  width:         2rem;
  height:        4rem;
  margin-bottom: 1rem;
`

const Divider = styled(Box)`
  opacity:       ${props => props.opacity};
  margin-top:    2rem;
  margin-bottom: 0rem;
  transition:    opacity 0.2s linear;
`;


export const Section = (observer((props: ISectionProps): JSX.Element => {
  const {title, currentUser, Card, CardAlt, onNew, onNewAlt, newIcon, newIconAlt, onAdd, onAddAlt, onRemove, onRemoveAlt, newToolTip, newToolTipAlt, linkTitle } = props;

  const { t, i18n } = useTranslation();

  const [showAddData, setShowAddData] = React.useState<boolean>(false);
  const data       = React.useMemo(() => props.sort ? props.data.sort(props.sort) : props.data, [props.sort, props.data]);
  const dataAlt    = React.useMemo(() => (props.sortAlt && props.dataAlt) ? props.dataAlt.sort(props.sortAlt) : props.dataAlt, [props.sortAlt, props.dataAlt]);
  const addData    = React.useMemo(() => props.sort ? props.addData.sort(props.sort) : props.addData, [props.sort, props.addData]);
  const addDataAlt = React.useMemo(() => props.sortAlt ? props.addDataAlt.sort(props.sortAlt) : props.addDataAlt, [props.sortAlt, props.addDataAlt]);

  const showAddButton: boolean = currentUser.canEdit && Boolean(props.onAdd && addData);

  return (
    <SectionPaper $noShadow>
      <Box>
        <StyledText>{title}</StyledText>
        <CardContainer>
          {data.map(d => <Card key={d.id} space_id={props.space_id} data={d} onUnlink={Boolean(onRemove) ? () => onRemove(d) : undefined} />)}
          {dataAlt && dataAlt.map(d => <CardAlt key={d.id} space_id={props.space_id} data={d} onUnlink={Boolean(onRemoveAlt) ? () => onRemoveAlt(d) : undefined} />)}
          {(Boolean(newIcon) && currentUser.canEdit) && (
            <EmptyCard
              onClick    = {onNew}
              icon       = {newIcon}
              tooltip    = {newToolTip}
              rightProps = {onNewAlt != null ? { onClick: onNewAlt, icon: newIconAlt, tooltip: newToolTipAlt } : undefined}
            />
          )}
        </CardContainer>
        {showAddButton && 
        <>
          <AddButton> 
            <Tooltip content={showAddData ? t("view_less_button"): t("view_more_button")}> 
            <ViewMoreButton variant="primary" onClick={() => setShowAddData(!showAddData)}>
              <Icon iconId={showAddData ? "remove" : "add"} size="large" cursor="pointer" color="#fff"/>
            </ViewMoreButton>
          </Tooltip>
          </AddButton>
          <Divider opacity={showAddData ? 1 : 0}><hr/></Divider>
          </>
        }
        {showAddData && 
         <Box>
          <StyledText>{linkTitle}</StyledText>
          <CardContainer>
            {addData.map(d => <Card key={d.id} space_id={props.space_id} data={d} onClick={() => onAdd(d)} />)}
            {addDataAlt && addDataAlt.map(d => <CardAlt key={d.id} space_id={props.space_id} data={d} onClick={() => onAddAlt(d)} />)}
          </CardContainer>
        </Box>
        }
      </Box>
    </SectionPaper>
  );
}));